import React, {useState} from "react";
import { useHistory } from "react-router-dom";
import { t } from "../common/LocalizationProvider";

import DialogTitle from "@material-ui/core/DialogTitle";
import {WarningOutlined} from "@material-ui/icons";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import {makeStyles} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 12,
        padding: theme.spacing(2), // Increased padding
        minHeight: '300px', // Slightly increased min height
        border: '3px solid #d32f2f', // Darker red border
        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)', // Added subtle shadow
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: '#d32f2f', // Darker red for title
        borderBottom: '1px solid #d32f2f',
        paddingBottom: theme.spacing(1.5), // Increased padding
        marginBottom: theme.spacing(2.5), // Increased margin
        fontSize: '1.4rem', // Larger title font size
        fontWeight: 600, // Bolder title
    },
    warningIcon: {
        color: '#d32f2f', // Darker red for icon
        fontSize: '2rem', // Larger icon
    },
    permanentText: {
        color: '#d32f2f', // Darker red for emphasis
        fontWeight: 'bold',
    },
    dialogContentText: {
        fontSize: '1rem', // Slightly larger font size for text
        marginBottom: theme.spacing(3), // More space below the text
    },
    textField: {
        marginBottom: theme.spacing(4),
        '& .MuiOutlinedInput-root': {
            borderRadius: 8, // Slightly rounded input fields
        },
    },
    dialogActions: {
        padding: theme.spacing(1), // Increased padding
        justifyContent: 'space-between',
        marginTop: theme.spacing(1), // More space above actions
        borderTop: '1px solid #eee', // Light border above actions
        paddingTop: theme.spacing(2),
    },
    cancelButton: {
        backgroundColor: '#6d4c41', // Darker brown
        color: 'white',
        '&:hover': {
            backgroundColor: '#4e342e', // Even darker brown on hover
        },
        padding: theme.spacing(1, 4), // More padding
        borderRadius: 8, // Rounded button
        fontWeight: 600,
    },
    deleteButton: {
        color: '#d32f2f', // Darker red
        border: '1px solid #d32f2f',
        '&:hover': {
            backgroundColor: '#ffebee', // Light red background on hover
            borderColor: '#b71c1c', // Darker red border on hover
        },
        padding: theme.spacing(1, 2), // Consistent padding
        borderRadius: 8, // Rounded button
        fontWeight: 600,
    },
}));


const DeleteAccountDialog = ({ requestDelete, setRequestDelete, setMessageSnacbar }) => {
    const classes = useStyles();
    const history = useHistory();
    const storedValue = localStorage.getItem('tokenThingwings');

    const [otpCode, setOtpCode] = useState(null);
    const [password, setPassword] = useState(null);
    const [showPassword, setShowPassword] = useState(false);


    const dialogMessage = () => {
        if (requestDelete === 'devicesExist')
            return <>
                There are <strong className={classes.permanentText}>active devices</strong> associated with your account.
                To continue, please remove them <strong>one by one</strong> and make sure each device is
                <strong className={classes.permanentText}> connected to the internet</strong>.
            </>;
        else if (requestDelete === 'noDevices')
            return <>
                To delete your account, please enter your <strong>password</strong>. We will send a verification code to your registered email.
            </>;
        else if (requestDelete === 'otpMessage')
            return <>
                A verification code has been sent to your email. Please enter the <strong>OTP code</strong> to permanently delete your account.
            </>
    }

    const handleDeleteAccountRequest = async () => {
        const response = await fetch('api/users/deleteAccount/request', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({password})
        })
        if (response.ok)
            setRequestDelete('otpMessage')
        else {
            const errorText = await response.text();
            let spliteText = errorText.split(' ')[0];
            setMessageSnacbar(spliteText ? spliteText : 'UnknownProblem')
        }
    }

    const handleDeleteAccountConfirm = async () => {
        const response = await fetch('api/users/deleteAccount/confirm', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Auth-Token': 'Bearer ' + storedValue,
            },
            body: JSON.stringify({
                otp: otpCode,
                password: password
            })
        })
        if (response.ok)
            history.push('/login')
        else {
            const errorText = await response.text();
            let spliteText = errorText.split(' ')[0];
            setMessageSnacbar(spliteText)
        }
    }


    return (
        <Dialog
            open={!!requestDelete}
            onClose={() => setRequestDelete(null)}
            PaperProps={{
                className: classes.dialogPaper
            }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <WarningOutlined className={classes.warningIcon} />
                {t('DeleteAccount')}?
            </DialogTitle>

            <DialogContent>
                <DialogContentText className={classes.dialogContentText}>
                    {dialogMessage()}
                </DialogContentText>

                {requestDelete !== 'otpMessage' &&
                    <TextField
                    autoFocus
                    fullWidth
                    label={t('userPassword')}
                    variant="outlined"
                    placeholder="password"
                    type={showPassword ? 'text' : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                    className={classes.textField}
                    disabled={requestDelete === 'devicesExist'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(prevState => !prevState)} edge="end">
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />}
                {requestDelete === 'otpMessage' &&
                    <TextField
                        fullWidth
                        label={t('VerifyOTP')}
                        variant="outlined"
                        placeholder="123456"
                        onChange={(e) => setOtpCode(e.target.value)}
                        className={classes.textField}
                />}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={() => setRequestDelete(null)}
                    variant="contained"
                    className={classes.cancelButton}
                >
                    {t('sharedCancel')}
                </Button>

                <Button
                    onClick={requestDelete !== 'otpMessage' ? handleDeleteAccountRequest : handleDeleteAccountConfirm}
                    variant="text"
                    className={classes.deleteButton}
                    disabled={requestDelete === 'devicesExist' || (password === null && otpCode === null)}
                >
                    {requestDelete !== 'otpMessage' ? t('delete') : t('Confirm') + ' ' + t('sharedshould_delete_N')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DeleteAccountDialog;